import { supabase } from './supabase';
import { toast } from 'react-hot-toast';
import type { User, UserRole } from '../types/auth';

export async function getAllUsers(): Promise<User[]> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error: any) {
    console.error('Error getting users:', error);
    toast.error('Failed to fetch users');
    return [];
  }
}

export async function createUser({ 
  email, 
  password, 
  displayName, 
  role = 'viewer' 
}: { 
  email: string; 
  password: string; 
  displayName: string; 
  role?: UserRole;
}): Promise<void> {
  try {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
          role
        }
      }
    });

    if (authError) throw authError;
    toast.success('User created successfully');
  } catch (error: any) {
    console.error('Error creating user:', error);
    toast.error(error.message);
    throw error;
  }
}

export async function updateUserRole(userId: string, role: UserRole): Promise<void> {
  try {
    const { error } = await supabase
      .from('users')
      .update({ role })
      .eq('id', userId);

    if (error) throw error;
    toast.success('User role updated');
  } catch (error: any) {
    console.error('Error updating user role:', error);
    toast.error(error.message);
    throw error;
  }
}

export async function toggleUserStatus(userId: string, disabled: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('users')
      .update({ disabled })
      .eq('id', userId);

    if (error) throw error;
    toast.success(`User ${disabled ? 'disabled' : 'enabled'}`);
  } catch (error: any) {
    console.error('Error toggling user status:', error);
    toast.error(error.message);
    throw error;
  }
}

export async function deleteUser(userId: string): Promise<void> {
  try {
    const { error } = await supabase.auth.admin.deleteUser(userId);
    if (error) throw error;
    toast.success('User deleted successfully');
  } catch (error: any) {
    console.error('Error deleting user:', error);
    toast.error(error.message);
    throw error;
  }
}